import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import DynamicTable from "../../components/DynamicTable";
import SearchBox from "../../components/SearchBox";
import Pagination from "../../components/Pagination";
import FilterDrawer from "../../components/FilterDrawer";
import { formatDateTime } from "../../utils/formateDate";
import apiHelper from "../../apiHelper";
import { Copy, MoreHorizontal, X, ChevronLeft, ChevronRight } from "lucide-react";
import noSpuImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import ConfirmDisableModal from '../../components/ConfirmDisableModal';
import AlertNotification from '../../components/AlertNotification';
import noImage from '../../assets/img/brand/noImage.png';
import Tooltip from "../addentities/Tooltip";
import noBrandImage from '../../assets/img/brand/no-brand-img.png';

export default function SpuSkuPage() {
    const { spu_id } = useParams();
    const location = useLocation();
    const history = useHistory();
    const [skuDrawerOpen, setSkuDrawerOpen] = useState(false);
    const [skuTab, setSkuTab] = useState('general');
    const [copied, setCopied] = useState(false);
    const drawerRef = useRef(null);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const archivedRef = useRef(null);
    const [disableModalOpen, setDisableModalOpen] = useState(false);
    const [actionSkuId, setActionSkuId] = useState(null);
    const activeTab = location.state?.activeTab || 'spu';
    const selectedSpu = location.state?.spu || null;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [archivedModalOpen, setArchivedModalOpen] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        type: '',
        recordName: '',
        action: ''
    });
    const breadcrumbs = ["Product", "SPU & SKU"];
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const PAGE_SIZE = 10;
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");
    const [selectedIds, setSelectedIds] = useState([]);
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [currentRow, setCurrentRow] = useState(null);
    const buttonRefs = useRef({});
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [suppressHighlight, setSuppressHighlight] = useState(false);
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const filterButtonRef = useRef(null);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [previewImages, setPreviewImages] = useState([]);
    const [previewIndex, setPreviewIndex] = useState(0);
    // Update columns when data changes
    useEffect(() => {
        if (data.length > 0) {
            setColumns(generateTableColumns());
        }
    }, [data]);
    // Get specification value for a specific SKU
    // Get specification value for a specific SKU
    const getSpecificationValue = (sku, attributeName) => {
        if (!sku?.specifications || !Array.isArray(sku.specifications)) return null;

        // Try to find with exact match first
        let spec = sku.specifications.find(s =>
            s.attribute_name === attributeName
        );

        // If not found, try case-insensitive match
        if (!spec) {
            const normalizedInput = attributeName.toLowerCase().replace(/\s+/g, ' ');
            spec = sku.specifications.find(s =>
                s.attribute_name && s.attribute_name.toLowerCase().replace(/\s+/g, ' ') === normalizedInput
            );
        }

        return spec?.attribute_value || null;
    };
    // Sorting function
    const sortData = (dataToSort, field, order) => {
        if (!field || !dataToSort) return dataToSort;

        const sorted = [...dataToSort].sort((a, b) => {
            let aValue = '';
            let bValue = '';

            // For dynamic specification columns
            if (field.startsWith('spec_')) {
                // Extract the attribute name from the field
                // Remove 'spec_' prefix and replace underscores with spaces
                const attributeName = field.replace('spec_', '').replace(/_/g, ' ');
                aValue = getSpecificationValue(a, attributeName) || '';
                bValue = getSpecificationValue(b, attributeName) || '';
            }
            // Handle enable_display field
            else if (field === 'enable_display') {
                const aEnabled = a.enable_display === true ||
                    a.enable_display === 'true' ||
                    a.enable_display === 'Enabled' ||
                    a.enable_display === 1;

                const bEnabled = b.enable_display === true ||
                    b.enable_display === 'true' ||
                    b.enable_display === 'Enabled' ||
                    b.enable_display === 1;

                aValue = aEnabled ? 'YES' : 'NO';
                bValue = bEnabled ? 'YES' : 'NO';
            }
            // Handle MSRP field (numeric sorting)
            else if (field === 'msrp') {
                aValue = a.msrp || 0;
                bValue = b.msrp || 0;
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }
            // Handle date fields
            else if (field === 'modified_at' || field === 'archived_at') {
                aValue = new Date(a[field] || 0).getTime();
                bValue = new Date(b[field] || 0).getTime();
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }
            // Handle other fields
            else {
                aValue = a[field] || '';
                bValue = b[field] || '';
            }

            // Convert to string for comparison (case-insensitive)
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();

            if (aStr < bStr) return order === 'asc' ? -1 : 1;
            if (aStr > bStr) return order === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    };

    // Add a helper function to get the actual attribute name with proper case
    const getSpecificationAttributeName = (sku, normalizedName) => {
        if (!sku?.specifications || !Array.isArray(sku.specifications)) return null;

        // Try to find the actual attribute name in the specifications
        const spec = sku.specifications.find(s =>
            s.attribute_name &&
            s.attribute_name.toLowerCase().replace(/\s+/g, '_') === normalizedName.toLowerCase()
        );

        return spec ? spec.attribute_name : null;
    };

    // Generate table columns dynamically based on available specifications
    // Generate table columns dynamically based on available specifications
    const generateTableColumns = () => {
        const baseColumns = [
            // { field: "sku_name", label: "SKU Name", sortable: true, width: "9%" },
            {
                field: "sku_name",
                label: "SKU Name",
                sortable: true,
                width: "9%",   // ← in %
                render: (row) => (
                    <div className="name-cell" style={{overflowX:'hidden'}}>
                        <img
                            src={row?.sku_image_name || noImage}
                            alt="avatar"
                            className="avatar"
                            style={{
                                width: 32,
                                height: 32,
                                marginRight: 8,
                            }}
                        />
                        {row.sku_name}
                    </div>
                ),
            },
            { field: "sku_code", label: "SKU Code", sortable: true, width: "8%" },
            ...(activeTab === 'spu' ? [{
                field: "enable_display",
                label: "Enable",
                sortable: true,
                width: "8%",
                render: (row) => {
                    const isEnabled =
                        row.enable_display === true ||
                        row.enable_display === "true" ||
                        row.enable_display === "Enabled" ||
                        row.enable_display === 1;

                    return (
                        <span
                            style={{
                                display: "inline-block",
                                height: '20px',
                                minWidth: '72px',
                                padding: "2px 10px",
                                borderRadius: "50px",
                                whiteSpace: "nowrap",
                                fontSize: "12px",
                                fontWeight: 600,
                                justifyContent: 'center',
                                textAlign: 'center',
                                border: isEnabled ? "1px solid #198754" : "1px solid #DC3545",
                                backgroundColor: isEnabled ? "#D1E7DD" : "#F8D7DA",
                                color: isEnabled ? "#198754" : "#DC3545",
                            }}
                        >
                            {isEnabled ? "YES" : "NO"}
                        </span>
                    );
                },
            }] : [])
        ];

        // Get all unique specification attribute names from all SKUs
        const getAllSpecificationAttributes = () => {
            if (!Array.isArray(data) || data.length === 0) return [];

            const attributeSet = new Set();

            data.forEach(sku => {
                if (sku.specifications && Array.isArray(sku.specifications)) {
                    sku.specifications.forEach(spec => {
                        if (spec.attribute_name) {
                            attributeSet.add(spec.attribute_name);
                        }
                    });
                }
            });

            return Array.from(attributeSet);
        };

        // Add dynamic specification columns
        const specificationAttributes = getAllSpecificationAttributes();
        // Update the generateTableColumns function - replace the specColumns mapping:
        // In the specColumns mapping in generateTableColumns:
        const specColumns = specificationAttributes.map((attrName, index) => {
            // Create a consistent field name - use lowercase and replace spaces with underscores
            const fieldName = `spec_${attrName.toLowerCase().replace(/\s+/g, '_')}`;

            return {
                field: fieldName,
                label: attrName, // Keep original label for display
                sortable: true,
                width: index === 2 ? "9%" : "8%",
                render: (row) => {
                    const value = getSpecificationValue(row, attrName); // Use original attrName here
                    return value ? (
                        <span
                            style={{
                                background: "#FFEB3B",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontWeight: 600,
                                fontSize: "13px",
                                color: "#000",
                                display: "inline-block",
                                minWidth: "60px",
                                textAlign: "center"
                            }}
                        >
                            {value}
                        </span>
                    ) : <span style={{ color: '#ADB5BD' }}>NA</span>;
                }
            };
        });

        const otherColumns = [
            {
                field: "gtin",
                label: (
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        GTIN <Tooltip title="GTIN" body="Global Trade Item Number of the SKU" >
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                <g clipPath="url(#clip0_3856_30308)">
                                    <path fillRule="evenodd" clipRule="evenodd"
                                        d="M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375   
                              6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594   
                              6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594   
                              2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688   
                              8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322   
                              7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784   
                              6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0   
                              3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z"
                                        fill="#5E6E82"
                                    />
                                    <path d="M5.58122 4.11719L4.14997 4.29656L4.09872 4.53406L4.37997 4.58594C4.56372 4.62969   
                            4.59997 4.69594 4.55997 4.87906L4.09872 7.04656C3.97747 7.60719 4.16435 7.87094   
                            4.60372 7.87094C4.94435 7.87094 5.33997 7.71344 5.51935 7.49719L5.57434 7.23719C5.44934   
                            7.34719 5.26684 7.39094 5.14559 7.39094C4.97372 7.39094 4.91122 7.27031 4.95559   
                            7.05781L5.58122 4.11719Z" fill="#5E6E82" />
                                    <path d="M5 3.4375C5.34518 3.4375 5.625 3.15768 5.625 2.8125C5.625 2.46732 5.34518 2.1875   
                            5 2.1875C4.65482 2.1875 4.375 2.46732 4.375 2.8125C4.375 3.15768 4.65482 3.4375   
                            5 3.4375Z" fill="#5E6E82" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_3856_30308">
                                        <rect width="10" height="10" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </Tooltip>
                    </span>
                ),
                sortable: true,
                width: "9%"
            },
            {
                field: "msrp",
                label: (
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        MSRP <Tooltip title="MSRP" body="Manufacturer's Suggested Retail Price of the SKU">
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                <g clipPath="url(#clip0_3856_30308)">
                                    <path fillRule="evenodd" clipRule="evenodd"
                                        d="M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375   
                              6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594   
                              6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594   
                              2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688   
                              8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322   
                              7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784   
                              6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0   
                              3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z"
                                        fill="#5E6E82"
                                    />
                                    <path d="M5.58122 4.11719L4.14997 4.29656L4.09872 4.53406L4.37997 4.58594C4.56372 4.62969   
                            4.59997 4.69594 4.55997 4.87906L4.09872 7.04656C3.97747 7.60719 4.16435 7.87094   
                            4.60372 7.87094C4.94435 7.87094 5.33997 7.71344 5.51935 7.49719L5.57434 7.23719C5.44934   
                            7.34719 5.26684 7.39094 5.14559 7.39094C4.97372 7.39094 4.91122 7.27031 4.95559   
                            7.05781L5.58122 4.11719Z" fill="#5E6E82" />
                                    <path d="M5 3.4375C5.34518 3.4375 5.625 3.15768 5.625 2.8125C5.625 2.46732 5.34518 2.1875   
                            5 2.1875C4.65482 2.1875 4.375 2.46732 4.375 2.8125C4.375 3.15768 4.65482 3.4375   
                            5 3.4375Z" fill="#5E6E82" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_3856_30308">
                                        <rect width="10" height="10" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </Tooltip>
                    </span>
                ),
                sortable: true,
                width: "8%",
                render: (row) => row.msrp ? `$${row.msrp}` : <span style={{ color: '#ADB5BD' }}>NA</span>
            },
            {
                field: activeTab === "spu" ? "modified_at" : "archived_at",
                label: activeTab === "spu" ? "Last Modified Time" : "Archived Time",
                sortable: true,
                width: "12%",
                render: (row) =>
                    activeTab === "spu"
                        ? formatDateTime(row.modified_at)
                        : formatDateTime(row.archived_at)
            }
        ];

        // Combine all columns
        return [...baseColumns, ...specColumns, ...otherColumns];
    };
    const [columns, setColumns] = useState(generateTableColumns());
    const getSpecificationFilterOptions = () => {
        if (!Array.isArray(data)) return {};

        const map = {};

        data.forEach(item => {
            if (!Array.isArray(item.specifications)) return;

            item.specifications.forEach(spec => {
                if (!spec.attribute_name || !spec.attribute_value) return;

                const key = spec.attribute_name
                    .toLowerCase()
                    .trim()
                    .replace(/\s+/g, "_"); // dynamic key

                if (!map[key]) {
                    map[key] = {
                        label: spec.attribute_name, // original name
                        values: new Set()
                    };
                }

                map[key].values.add(spec.attribute_value);
            });
        });

        // convert Set → Array
        Object.keys(map).forEach(key => {
            map[key].values = Array.from(map[key].values);
        });

        return map;
    };

    const sections = useMemo(() => {
        const specMap = getSpecificationFilterOptions();

        const dynamicSpecSections = Object.keys(specMap).map(key => ({
            label: specMap[key].label.toUpperCase(),
            type: "list",
            key,
            data: specMap[key].values.map(v => ({
                id: v,
                title: v
            })),
            renderItem: item => ({
                id: item.id,
                title: item.title
            })
        }));

        return [
            ...(activeTab === "spu"
                ? [{
                    label: "Enable",
                    type: "checkbox",
                    key: "status",
                    options: ["YES", "NO"]
                }]
                : []),

            ...dynamicSpecSections,

            {
                label: "MSRP",
                type: "range",
                key: "msrp"
            }
        ];
    }, [data, activeTab]);
    useEffect(() => {
        const { temp, applied } = buildEmptyFilterState(sections);
        setTempValues(temp);
        setFilters(applied);
    }, [sections]);
    // Also update the date fields check to include archived_at for completeness
    const buildEmptyFilterState = (sections) => {
        const temp = {};
        const applied = {};

        sections.forEach(section => {
            if (section.key === "msrp") {
                temp.msrp_min = "";
                temp.msrp_max = "";
                applied.msrp = { min: null, max: null };
            } else {
                temp[section.key] = [];
                applied[section.key] = [];
            }
        });

        return { temp, applied };
    };

    const { temp: initialTemp, applied: initialFilters } = useMemo(
        () => buildEmptyFilterState(sections),
        [sections]
    );
    const [tempValues, setTempValues] = useState(initialTemp);
    const [filters, setFilters] = useState(initialFilters);
    const normalizeSpecKey = (name = "") => {
        return name
            .toLowerCase()
            .trim()
            .replace(/\s+/g, "_");
    };
    // Apply search, filtering, and sorting
    const filteredAndSorted = useMemo(() => {
        let result = data || [];

        // Apply search filter
        if (searchTerm && searchTerm.trim() !== '') {
            const searchLower = searchTerm.toLowerCase().trim();
            result = result.filter(item => {
                // Search in specific fields
                const searchFields = [
                    item.sku_name,
                    item.sku_code,
                    item.gtin,
                    item.msrp?.toString(),
                    // Add specification values
                    ...(item.specifications?.map(spec =>
                        `${spec.attribute_name} ${spec.attribute_value}`
                    ) || [])
                ].filter(Boolean); // Remove null/undefined

                // Check each field
                return searchFields.some(field =>
                    field.toLowerCase().includes(searchLower)
                );
            });
        }

        // ... rest of your existing filtering code (status, color, ram, storage, msrp)
        // ✅ STATUS FILTER (YES / NO → boolean)
        if(activeTab == 'spu'){
            if (filters.status.length) {
        // if (filters.status) {
            result = result.filter(item => {
                const isEnabled =
                    item.enable_display === true ||
                    item.enable_display === "true" ||
                    item.enable_display === "Enabled" ||
                    item.enable_display === 1;
                if (filters.status.includes("YES") && isEnabled) return true;
                if (filters.status.includes("NO") && !isEnabled) return true;
                return false;
            });
        }
        }
        

        // 🔥 APPLY ALL SPEC FILTERS DYNAMICALLY
        Object.keys(filters).forEach(key => {
            if (["status", "msrp"].includes(key)) return;
            if (!filters[key]?.length) return;

            result = result.filter(item =>
                item.specifications?.some(spec =>
                    normalizeSpecKey(spec.attribute_name) === key &&
                    filters[key].includes(spec.attribute_value)
                )
            );
        });


        // ✅ MSRP RANGE
        if (filters.msrp.min != null || filters.msrp.max != null) {
            result = result.filter(item => {
                const price = Number(item.msrp);
                if (isNaN(price)) return false;
                return (
                    (filters.msrp.min != null ? price >= filters.msrp.min : true) &&
                    (filters.msrp.max != null ? price <= filters.msrp.max : true)
                );
            });
        }

        // SORT - With default sorting when no sortField is set
        if (sortField) {
            // User has clicked on a column - use their preference
            result = sortData(result, sortField, sortOrder);
        } else {
            // Apply default sorting based on activeTab
            const defaultSortField = activeTab === "spu" ? "modified_at" : "archived_at";
            const defaultSortOrder = "desc"; // Latest first for both cases

            console.log(`Applying default sorting: ${defaultSortField} (${defaultSortOrder})`);
            result = sortData(result, defaultSortField, defaultSortOrder);
        }

        return result;
    }, [data, searchTerm, sortField, sortOrder, filters, activeTab]);
    const isSearching = searchTerm.trim().length > 0;
    const totalPages = Math.ceil(filteredAndSorted.length / PAGE_SIZE);
    useEffect(() => {
        const query = new URLSearchParams(location.search);

        const autoOpen = query.get("autoOpen") === "true";
        const skuId = query.get("id");
        const tab = query.get("tab");

        if (autoOpen && skuId && data.length > 0) {
            console.log('hello');
            const sku = data.find(item => item.sku_id == skuId);

            if (sku) {
                console.log('Hello1');
                setSelectedRow(sku);
                setSkuDrawerOpen(true);
                setSkuTab(tab || "general");
            }
            else {
                console.log('byy1');
            }
        } else {
            console.log('byy');
        }
    }, [location.search, data]);

    useEffect(() => {
        if (!spu_id) return;

        async function fetchArchivedSpus() {
            try {
                console.log("API CALL:", `inventory/sku/spu/${spu_id}/`);

                const response = await apiHelper(`inventory/sku/spu/${spu_id}/`);
                if (response?.ok) {
                    const result = await response.json();
                    console.log("SKUs:", result);
                    setData(result);
                }
            } catch (error) {
                console.error("Failed to fetch SKUs:", error);
            } finally {
                setLoading(false);
            }
        }

        fetchArchivedSpus();
    }, [spu_id]);
    // Add this useEffect to set default sorting when data loads or activeTab changes
    useEffect(() => {
        if (data.length > 0) {
            // Set default sort field based on activeTab
            if (activeTab === 'spu') {
                // For active SKUs: Last Modified Time (latest first)
                setSortField('modified_at');
                setSortOrder('desc'); // Latest first = descending
            } else {
                // For archived SKUs: Archived Time (latest first)
                setSortField('archived_at');
                setSortOrder('desc'); // Latest first = descending
            }
        }
    }, [data, activeTab]); // Re-run when data or activeTab changes

    const [toolbarDropdownOpen, setToolbarDropdownOpen] = useState(false);
    const toolbarRef = useRef(null);
    const toggleToolbarDropdown = () => {
        setToolbarDropdownOpen(prev => !prev);
    };
    const filtersApplied = useMemo(() => {
        return Object.entries(filters).some(([key, value]) => {
            // status → array
            if (Array.isArray(value)) {
                return value.length > 0;
            }

            // msrp → range object
            if (key === "msrp") {
                return value?.min != null || value?.max != null;
            }

            return false;
        });
    }, [filters]);

    // Get all unique specification attribute names from all SKUs
    const getAllSpecificationAttributes = () => {
        if (!Array.isArray(data) || data.length === 0) return [];

        const attributeSet = new Set();

        data.forEach(sku => {
            if (sku.specifications && Array.isArray(sku.specifications)) {
                sku.specifications.forEach(spec => {
                    if (spec.attribute_name) {
                        attributeSet.add(spec.attribute_name);
                    }
                });
            }
        });

        return Array.from(attributeSet);
    };



    // Get specifications for a SKU (for drawer display)
    const getSKUSpecifications = (sku) => {
        if (!sku?.specifications || !Array.isArray(sku.specifications)) return [];

        return sku.specifications.map(spec => ({
            name: spec.attribute_name,
            value: spec.attribute_value,
            id: spec.specification_id
        }));
    };

    // Sorting function
    // const sortData = (dataToSort, field, order) => {
    //     if (!field || !dataToSort) return dataToSort;

    //     const sorted = [...dataToSort].sort((a, b) => {
    //         let aValue = a[field];
    //         let bValue = b[field];

    //         // For dynamic specification columns
    //         if (field.startsWith('spec_')) {
    //             const attributeName = field.replace('spec_', '').replace(/_/g, ' ');
    //             aValue = getSpecificationValue(a, attributeName);
    //             bValue = getSpecificationValue(b, attributeName);
    //         }

    //         // Handle enable_display field
    //         if (field === 'enable_display') {
    //             const aEnabled = a.enable_display === true ||
    //                 a.enable_display === 'true' ||
    //                 a.enable_display === 'Enabled' ||
    //                 a.enable_display === 1;

    //             const bEnabled = b.enable_display === true ||
    //                 b.enable_display === 'true' ||
    //                 b.enable_display === 'Enabled' ||
    //                 b.enable_display === 1;

    //             aValue = aEnabled ? 'YES' : 'NO';
    //             bValue = bEnabled ? 'YES' : 'NO';
    //         }

    //         // Handle MSRP field (numeric sorting)
    //         if (field === 'msrp') {
    //             aValue = a.msrp || 0;
    //             bValue = b.msrp || 0;
    //             return order === 'asc' ? aValue - bValue : bValue - aValue;
    //         }

    //         // Handle date fields
    //         if (field === 'modified_at') {
    //             aValue = new Date(a.modified_at || 0).getTime();
    //             bValue = new Date(b.modified_at || 0).getTime();
    //             return order === 'asc' ? aValue - bValue : bValue - aValue;
    //         }

    //         // Handle undefined/null values
    //         if (aValue == null) aValue = '';
    //         if (bValue == null) bValue = '';

    //         // Convert to string for comparison
    //         const aStr = String(aValue).toLowerCase();
    //         const bStr = String(bValue).toLowerCase();

    //         if (aStr < bStr) return order === 'asc' ? -1 : 1;
    //         if (aStr > bStr) return order === 'asc' ? 1 : -1;
    //         return 0;
    //     });

    //     return sorted;
    // };

    const filterOptions = () => {
        const result = {
            color: new Set(),
            ram: new Set(),
            storage: new Set()
        };

        if (Array.isArray(data)) {
            data.forEach(sku => {
                if (sku.specifications && Array.isArray(sku.specifications)) {
                    sku.specifications.forEach(spec => {
                        if (spec.attribute_name && spec.attribute_value) {
                            const attrName = spec.attribute_name.toLowerCase();
                            if (attrName.includes('color')) {
                                result.color.add(spec.attribute_value);
                            } else if (attrName.includes('ram') || attrName.includes('memory')) {
                                result.ram.add(spec.attribute_value);
                            } else if (attrName.includes('storage') || attrName.includes('capacity')) {
                                result.storage.add(spec.attribute_value);
                            }
                        }
                    });
                }
            });
        }

        return {
            color: [...result.color],
            ram: [...result.ram],
            storage: [...result.storage]
        };
    };

    const pageData = filteredAndSorted.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );
    const isInitialTableEmpty = data.length === 0;

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const isRedirectedFromAnotherPage = location.search.includes("from=stocklist");

    // const sections = [
    //     ...(activeTab === "spu"
    //         ? [{
    //             label: "Enable",
    //             type: "checkbox",
    //             key: "status",
    //             options: ["YES", "NO"]
    //         }]
    //         : []),

    //     {
    //         label: "Color",
    //         type: "list",
    //         key: "color",
    //         data: (filterOptions().color || []).map(v => ({
    //             id: v,
    //             title: v
    //         })),
    //         renderItem: item => ({
    //             id: item.id,
    //             title: item.title
    //         })
    //     },

    //     {
    //         label: "RAM",
    //         type: "list",
    //         key: "ram",
    //         data: (filterOptions().ram || []).map(v => ({
    //             id: v,
    //             title: v
    //         })),
    //         renderItem: item => ({
    //             id: item.id,
    //             title: item.title
    //         })
    //     },

    //     {
    //         label: "Storage",
    //         type: "list",
    //         key: "storage",
    //         data: (filterOptions().storage || []).map(v => ({
    //             id: v,
    //             title: v
    //         })),
    //         renderItem: item => ({
    //             id: item.id,
    //             title: item.title
    //         })
    //     },

    //     {
    //         label: "MSRP",
    //         type: "range",
    //         key: "msrp"
    //     }
    // ];

    // Image selection
    const skuEmptyImage = isInitialTableEmpty
        ? noSpuImage
        : emptySearchImg;

    // Title selection
    const skuEmptyTitle = isInitialTableEmpty
        ? "No SKUs Found"
        : isSearching
            ? "No Results Found"
            : "No SKUs Match Filters";

    // Message selection
    const skuEmptyMessage = isInitialTableEmpty
        ? "This SPU does not have any SKUs yet."
        : isSearching
            ? "No SKUs match what you're searching for."
            : "No SKUs match your applied filters.";

    /* ------------------ TABLE PROCESSING ------------------ */
    const toggleSelect = () => { };
    const toggleAll = () => { };


    const handleCopy = async (textToCopy) => {
        if (!textToCopy) {
            console.error('No text to copy');
            alert('Failed to copy: No text available.');
            return;
        }

        if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            try {
                await navigator.clipboard.writeText(textToCopy);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error('Clipboard API failed:', err);
                fallbackCopyTextToClipboard(textToCopy);
            }
        } else {
            console.warn('Clipboard API not available or not in secure context, using fallback');
            fallbackCopyTextToClipboard(textToCopy);
        }
    };

    const fallbackCopyTextToClipboard = (text) => {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } else {
                console.error('Fallback copy failed');
                alert('Failed to copy text. Please copy manually.');
            }
        } catch (err) {
            console.error('Fallback copy error:', err);
            alert('Failed to copy text. Please copy manually.');
        }
    };

    const handleViewSku = (row) => {
        setSelectedRow(row);
        console.log("Viewing SKU:", row);
        setSkuDrawerOpen(true);
        setSkuTab('general');
        setDropdownOpenId(null);
    };

    const handleArchivedConfirm = () => {
        console.log("Archiving:", selectedEmployeeId);
        setArchivedModalOpen(false);
    };

    const isBrandNameOverflowing = (brandName) => {
        if (!brandName) return false;
        return brandName.length > 30;
    };

    if (loading) return <p>Loading...</p>;

    // Handler for SKU enable/disable actions
    const handleSkuEnableDisableClick = async (skuId) => {
        const skuToUpdate = data.find(sku => sku.sku_id === skuId);
        if (!skuToUpdate) {
            console.error('SKU not found');
            return;
        }

        const isEnabled = skuToUpdate.enable_display === true ||
            skuToUpdate.enable_display === 'true' ||
            skuToUpdate.enable_display === 'Enabled' ||
            skuToUpdate.enable_display === 1;

        if (isEnabled) {
            setActionSkuId(skuId);
            setDisableModalOpen(true);
        } else {
            await handleSkuStatusChange(skuId, true);
        }
    };

    // Handler for actual SKU status change
    const handleSkuStatusChange = async (skuId, enable) => {
        try {
            const response = await apiHelper(
                `inventory/sku/${skuId}/update/`,
                'PUT',
                { enable_display: enable }
            );

            if (response.ok) {
                setData(prev => prev.map(sku =>
                    sku.sku_id === skuId
                        ? {
                            ...sku,
                            enable_display: enable
                        }
                        : sku
                ));

                if (selectedRow && selectedRow.sku_id === skuId) {
                    setSelectedRow(prev => ({
                        ...prev,
                        enable_display: enable
                    }));
                }

                setNotification({
                    show: true,
                    type: 'success',
                    recordName: 'SKU',
                    action: enable ? 'enable' : 'disable'
                });

                setTimeout(() => {
                    setNotification({ show: false, type: '', recordName: '', action: '' });
                }, 5000);
            } else {
                setNotification({
                    show: true,
                    type: 'error',
                    recordName: 'SKU',
                    action: enable ? 'enable' : 'disable'
                });
            }
        } catch (error) {
            console.error('Error updating SKU status:', error);
            setNotification({
                show: true,
                type: 'error',
                recordName: 'SKU',
                action: 'update'
            });
        } finally {
            setDisableModalOpen(false);
            setActionSkuId(null);
        }
    };

    // Handler for confirming disable (from modal)
    const handleConfirmDisable = async () => {
        await handleSkuStatusChange(actionSkuId, false);
    };

    // Handler for closing notification
    const handleCloseNotification = () => {
        setNotification({ show: false, type: '', recordName: '', action: '' });
    };

    // Update the dropdown action button in the table:
    const toggleDropdown = (id) => {
        console.log("Toggling dropdown for ID:", id);

        const clickedRow = data.find(item => item.sku_id === id);
        setCurrentRow(clickedRow);

        if (dropdownOpenId === id) {
            setDropdownOpenId(null);
            return;
        }

        const btn = buttonRefs.current[id];
        if (!btn) {
            console.warn("Button ref missing for ID:", id);
            setDropdownOpenId(id);
            return;
        }

        const rect = btn.getBoundingClientRect();
        const dropdownWidth = 180;
        const dropdownHeight = 140;

        let left = rect.left + window.scrollX - dropdownWidth + rect.width;
        if (left < 10) left = 10;

        let top = rect.bottom + window.scrollY;
        if (top + dropdownHeight > window.scrollY + window.innerHeight) {
            top = rect.top + window.scrollY - dropdownHeight;
        }

        setDropdownPosition({ top, left });
        setDropdownOpenId(id);
    };

    const FilterActiveIcon = () => (
        <svg style={{ margin: 0 }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <g clipPath="url(#clip0_4516_4830)">
                <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
            </g>
            <defs>
                <clipPath id="clip0_4516_4830">
                    <rect width="12" height="12" fill="white" transform="translate(11 5)" />
                </clipPath>
            </defs>
        </svg>
    );

    const FilterInactiveIcon = () => (
        <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    // Get current SKU specifications for drawer
    const getCurrentSKUSpecifications = () => {
        if (!selectedRow) return [];
        return getSKUSpecifications(selectedRow);
    };

    // Get configuration specifications
    const getConfigurationSpecs = () => {
        if (!selectedRow?.configuration?.specifications) return [];
        return selectedRow.configuration.specifications;
    };

    return (
        <div className="company-body main-company-body-inner new-look">

            {/* ---------- Breadcrumb ---------- */}
            <div className="breadcrumb">
                {breadcrumbs
                    .concat(selectedSpu?.model_name || "SPU Details")
                    .map((crumb, index, arr) => (
                        <span key={index} className="breadcrumb-item">
                            {crumb}
                            {index !== arr.length - 1}
                        </span>
                    ))}
            </div>

            <div className="employee-tabs-container">
                <div className="tab-content" id="spu-content">
                    <div className="info-section">
                        <div
                            className="edit-organisation-info"
                            style={{
                                display: "flex",
                                width: "100%",
                                padding: "10px",
                                gap: "5px",
                                borderRadius: "8px",
                                background: "#FFF",
                                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.10)",
                                flexWrap: "wrap"
                            }}
                        >
                            {/* ICON WRAPPER (flex, padding, centered) */}
                            <div
                                style={{
                                    display: "flex",
                                    minWidth: "88px",
                                    padding: "10px",
                                    justifyContent: "center",
                                    gap: "10px",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    flexShrink: 1
                                }}
                                onClick={() => history.goBack()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M18 12C18 11.8011 17.921 11.6103 17.7803 11.4696C17.6397 11.329 
                17.4489 11.25 17.25 11.25H8.5605L11.781 8.03097C11.9218 7.89014 12.0009 
                7.69913 12.0009 7.49997C12.0009 7.30081 11.9218 7.1098 11.781 
                6.96897C11.6402 6.82814 11.4492 6.74902 11.25 6.74902C11.0508 
                6.74902 10.8598 6.82814 10.719 6.96897L6.219 11.469C6.14915 
                11.5386 6.09374 11.6214 6.05593 11.7125C6.01812 11.8036 
                5.99866 11.9013 5.99866 12C5.99866 12.0986 6.01812 12.1963 
                6.05593 12.2874C6.09374 12.3785 6.14915 12.4613 6.219 
                12.531L10.719 17.031C10.8598 17.1718 11.0508 17.2509 11.25 
                17.2509C11.4492 17.2509 11.6402 17.1718 11.781 17.031C11.9218 
                16.8901 12.0009 16.6991 12.0009 16.5C12.0009 16.3008 11.9218 
                16.1098 11.781 15.969L8.5605 12.75H17.25C17.4489 12.75 17.6397 
                12.671 17.7803 12.5303C17.921 12.3896 18 12.1989 18 12Z"
                                        fill="#1F1F1F"
                                    />
                                </svg>
                                <h4
                                    className="edit-organisation-left"
                                    style={{
                                        color: "#212529",
                                        textAlign: "center",
                                        fontFamily: "Montserrat",
                                        fontSize: "20px",
                                        fontStyle: "normal",
                                        fontWeight: 700,
                                        lineHeight: "normal",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word"
                                    }}
                                >
                                    {selectedSpu?.model_name || "SPU Details"} SKUs
                                </h4>
                            </div>
                        </div>

                        <div className="info-card">
                            <div className="entities-tab">
                                <div className="toolbar">
                                    <div className="toolbar-left">
                                        <SearchBox
                                            searchInput={searchInput}
                                            setSearchInput={setSearchInput}
                                            setSearchTerm={setSearchTerm}
                                            setCurrentPage={setCurrentPage}
                                            disabled={isInitialTableEmpty}
                                        />
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                ref={filterButtonRef}
                                                className={`icon-btn p-2 filter-btn ${filtersApplied ? "applied" : ""}`}
                                                onClick={() => !isInitialTableEmpty && setFilterPopupOpen(!filterPopupOpen)}
                                                disabled={isInitialTableEmpty}
                                                style={{
                                                    border: filtersApplied ? "1px solid #2C7BE5" : "1px solid #6C757D",
                                                    borderWidth: "1px",
                                                    color: filtersApplied ? "#2C7BE5" : "#6C757D",
                                                    background: isInitialTableEmpty
                                                        ? "#E9ECEF"
                                                        : filtersApplied
                                                            ? "#CFE2FF"
                                                            : "#FFFFFF",
                                                    height: "40px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    opacity: isInitialTableEmpty ? 0.6 : 1,
                                                    pointerEvents: isInitialTableEmpty ? "none" : "auto",
                                                    cursor: isInitialTableEmpty ? "not-allowed" : "pointer",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                {filtersApplied && !isInitialTableEmpty ? (
                                                    <FilterActiveIcon />
                                                ) : (
                                                    <FilterInactiveIcon />
                                                )}
                                            </button>
                                        </div>
                                        <FilterDrawer
                                            isOpen={filterPopupOpen}
                                            onClose={() => {
                                                const { temp, applied } = buildEmptyFilterState(sections);
                                                setTempValues(temp);
                                                setFilters(applied);
                                                setFilterPopupOpen(false);
                                            }}
                                            sections={sections}
                                            tempValues={tempValues}
                                            setTempValues={setTempValues}
                                            onApply={() => {
                                                const applied = {};

                                                sections.forEach(section => {
                                                    if (section.key === "msrp") {
                                                        applied.msrp = {
                                                            min: tempValues.msrp_min
                                                                ? Number(tempValues.msrp_min)
                                                                : null,
                                                            max: tempValues.msrp_max
                                                                ? Number(tempValues.msrp_max)
                                                                : null
                                                        };
                                                    } else {
                                                        applied[section.key] = tempValues[section.key] || [];
                                                    }
                                                });

                                                setFilters(applied);
                                                setFilterPopupOpen(false);
                                            }}
                                            onClear={() => {
                                                const { temp, applied } = buildEmptyFilterState(sections);
                                                setTempValues(temp);
                                                setFilters(applied);
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="table-wrapper" style={{ border: "none" }}>
                                    <DynamicTable
                                        columns={columns}
                                        data={pageData}
                                        rowKey="sku_id"
                                        sortField={sortField}
                                        sortOrder={sortOrder}
                                        handleSort={handleSort}
                                        onRowClick={handleViewSku}
                                        showCheckbox={false}
                                        selectedIds={selectedIds}
                                        toggleSelect={toggleSelect}
                                        toggleAll={toggleAll}
                                        dropdownOpenId={dropdownOpenId}
                                        suppressHighlight={suppressHighlight}
                                        buttonRefs={buttonRefs}
                                        onActionClick={(id) => toggleDropdown(id)}
                                        emptyImage={skuEmptyImage}
                                        emptyTitle={skuEmptyTitle}
                                        emptyMessage={skuEmptyMessage}
                                        showActions={true}
                                    />
                                    {dropdownOpenId !== null && (
                                        <div
                                            ref={dropdownRef}
                                            className="backdrop-cls"
                                            style={{
                                                position: "absolute",
                                                top: dropdownPosition.top,
                                                left: dropdownPosition.left,
                                                background: "white",
                                                boxShadow: "0px 1px 4px 0px #0000001A",
                                                zIndex: 1000,
                                                minWidth: 152,
                                                borderRadius: 8,
                                            }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <div className="btn-align-forall">
                                                <div className="common-for-all">
                                                    <button onClick={() => {
                                                        handleViewSku(currentRow);
                                                    }}>
                                                        View SKU Details
                                                    </button>
                                                </div>

                                                {/* Fixed : No need in action menu  for archived spu */}
                                                <div className="common-for-all">
                                                    {currentRow && activeTab == 'spu' && (
                                                        <button onClick={() => {
                                                            handleSkuEnableDisableClick(currentRow.sku_id);
                                                            setDropdownOpenId(null);
                                                        }}>
                                                            {currentRow.enable_display === true ||
                                                                currentRow.enable_display === 'true' ||
                                                                currentRow.enable_display === 'Enabled' ||
                                                                currentRow.enable_display === 1 ? 'Disable' : 'Enable'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="pagination">
                                    <div className="pagination-info">
                                        {`${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, filteredAndSorted.length)} of ${filteredAndSorted.length} `}
                                    </div>
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>

                                {skuDrawerOpen && selectedRow && (
                                    <>
                                        <div className="drawer-overlay" onMouseDown={(e) => {
                                            console.log("🔥 OVERLAY CLICKED");
                                            setSkuDrawerOpen(false);
                                        }}
                                        />
                                        <div className="entity-drawer" ref={drawerRef}>
                                            <div className="drawer-header" style={{ background: "#F8F9FA" }}>
                                                <div className="d-flex align-items-center gap-1">
                                                    {(location.search.includes('from=stocklist')) && (
                                                        <button
                                                            className="back-btn"
                                                            onClick={() => {
                                                                if (location.search.includes('from=stocklist')) {
                                                                    const query = new URLSearchParams(location.search);
                                                                    const stockId = query.get('stockId');
                                                                    const stockTab = query.get('stockTab');
                                                                    const stockArchived = query.get('archived');
                                                                    history.push(`/stock-list?id=${stockId}&autoOpen=true&archived=${stockArchived}&tab=${stockTab}`);
                                                                    setSkuDrawerOpen(false);
                                                                    setSelectedRow(null);
                                                                }
                                                            }}
                                                            style={{ backgroundColor: 'transparent', border: 'none', padding: 0, margin: 0 }}
                                                        >
                                                            <ChevronLeft size={24} />
                                                        </button>
                                                    )}
                                                    <h3>SKU Info</h3>
                                                </div>
                                                <button
                                                    className="close-btn"
                                                    onClick={() => {
                                                        if (isRedirectedFromAnotherPage) return;
                                                        setSkuDrawerOpen(false);
                                                    }}
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>

                                            <div className="main-drawer-inner">
                                                <div className="drawer-title d-flex align-items-center justify-content-between" style={{
                                                    borderBottom: "none",
                                                    paddingBottom: "15px",
                                                    boxShadow: "none",
                                                    marginBottom: "0px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    flexWrap: "nowrap",
                                                    width: "100%",
                                                }}>
                                                    <div className="d-flex align-items-center">
                                                        <img
                                                            src={
                                                                selectedRow?.sku_image_name
                                                                    ? selectedRow?.sku_image_name
                                                                    : noImage
                                                            }
                                                            alt="avatar"
                                                            className="avatar"
                                                            style={{ width: 40, height: 40, marginRight: 8 }}
                                                        />
                                                        <h2 style={{ textTransform: 'none', }} className="pb-0">{selectedRow?.sku_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</h2>
                                                    </div>
                                                    <div className='entities-tab'>
                                                        <div className='toolbar'>
                                                            <div className="toolbar-right" style={{ position: "relative", flexWrap: "nowrap" }} ref={toolbarRef}>
                                                                {/* Fixed : No need in drawer for archived spu */}
                                                                {activeTab == 'spu' && (
                                                                    <button
                                                                        className="drawer-dots"
                                                                        style={{ boxShadow: 'none' }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleToolbarDropdown();
                                                                        }}
                                                                    >
                                                                        <MoreHorizontal size={18} />
                                                                    </button>
                                                                )}
                                                                {toolbarDropdownOpen && (
                                                                    <div
                                                                        className="backdrop-cls"
                                                                        style={{
                                                                            position: "absolute",
                                                                            top: 40,
                                                                            right: 0,
                                                                            background: "white",
                                                                            boxShadow: "0px 1px 4px 0px #0000001A",
                                                                            zIndex: 1000,
                                                                            minWidth: 152,
                                                                            borderRadius: 8,
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <div className="btn-align-forall">
                                                                            <div className="common-for-all">
                                                                                <button onClick={() => {
                                                                                    if (selectedRow) {
                                                                                        handleSkuEnableDisableClick(selectedRow.sku_id);
                                                                                    }
                                                                                    setToolbarDropdownOpen(false);
                                                                                }}>
                                                                                    {selectedRow?.enable_display === true ||
                                                                                        selectedRow?.enable_display === 'true' ||
                                                                                        selectedRow?.enable_display === 'Enabled' ||
                                                                                        selectedRow?.enable_display === 1 ? 'Disable' : 'Enable'}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="drawer-tabs" style={{
                                                    padding: "10px",
                                                    borderBottom: "none",
                                                    gap: "20px",
                                                }}>
                                                    <button
                                                        className={skuTab === 'general' ? 'active' : ''}
                                                        onClick={() => setSkuTab('general')}
                                                    >
                                                        General
                                                    </button>
                                                    <button
                                                        className={skuTab === 'configuration' ? 'active' : ''}
                                                        onClick={() => setSkuTab('configuration')}
                                                    >
                                                        Configuration
                                                    </button>
                                                </div>

                                                <div className="drawer-content">
                                                    {skuTab === 'general' && (
                                                        <div className="tab-panel general-panel">
                                                            <div
                                                                className="card-section"
                                                                style={{
                                                                    borderRadius: '12px',
                                                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                                    overflow: 'hidden',
                                                                    marginBottom: '20px',
                                                                    marginTop: '20px',
                                                                }}
                                                            >
                                                                <div
                                                                    className="card-header"
                                                                    style={{
                                                                        backgroundColor: ' #F8F9FA',
                                                                        padding: '15px 20px',
                                                                        fontFamily: 'Montserrat',
                                                                        fontSize: '14px',
                                                                        fontWeight: 600,
                                                                        color: ' #212529',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        alignSelf: 'stretch',
                                                                        borderRadius: '12px 12px 0px 0px',
                                                                        gap: '10px',
                                                                        height: '50px',
                                                                        borderBottom: 'none',
                                                                    }}
                                                                >
                                                                    Basic Info
                                                                </div>
                                                                <div
                                                                    className="card-body"
                                                                    style={{
                                                                        backgroundColor: '#FFFFFF',
                                                                        padding: '20px',
                                                                        display: 'grid',
                                                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                                                        gap: '20px',
                                                                    }}
                                                                >
                                                                    <div className="field-block" style={{
                                                                        borderBottom: 'none',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        padding: '0px',
                                                                        gridColumn: isBrandNameOverflowing(selectedRow.sku_name) ? '1 / -1' : '1',
                                                                        gridRow: '1'
                                                                    }}>
                                                                        <div className="field-label">SKU Name</div>
                                                                        <div className="field-value" style={{
                                                                            wordWrap: 'break-word',
                                                                            overflowWrap: 'break-word',
                                                                            whiteSpace: 'normal'
                                                                        }}>
                                                                            {selectedRow.sku_name || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </div>
                                                                    </div>
                                                                    <div className="field-block" style={{
                                                                        borderBottom: 'none',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        padding: '0px',
                                                                        gridColumn: isBrandNameOverflowing(selectedRow.sku_name) ? '1' : '2',
                                                                        gridRow: isBrandNameOverflowing(selectedRow.sku_name) ? '2' : '1'
                                                                    }}>
                                                                        <div className="field-label">SKU Code</div>
                                                                        <div className="field-value" style={{
                                                                            wordWrap: 'break-word',
                                                                            overflowWrap: 'break-word',
                                                                            whiteSpace: 'normal'
                                                                        }}>
                                                                            {selectedRow.sku_code || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </div>
                                                                    </div>
                                                                    {/* Dynamic Specifications */}
                                                                    {getCurrentSKUSpecifications().map((spec, index) => (
                                                                        <div key={index} className="field-block" style={{
                                                                            borderBottom: 'none',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            padding: '0px',
                                                                        }}>
                                                                            <div className="field-label">{spec.name}</div>
                                                                            <div className="field-value">
                                                                                {spec.value ? (
                                                                                    <span
                                                                                        style={{
                                                                                            background: "#FFEB3B",
                                                                                            padding: "6px 18px",
                                                                                            borderRadius: "20px",
                                                                                            fontWeight: 600,
                                                                                            fontSize: "13px",
                                                                                            color: "#000",
                                                                                            display: "inline-block",
                                                                                            minWidth: "60px",
                                                                                            textAlign: "center"
                                                                                        }}
                                                                                    >
                                                                                        {spec.value}
                                                                                    </span>
                                                                                ) : <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                            </div>
                                                                        </div>
                                                                    ))}

                                                                    <div
                                                                        className="field-block"
                                                                        style={{
                                                                            borderBottom: 'none',
                                                                            padding: '9px 0',
                                                                            gridColumn: '1 / -1',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'flex-start',
                                                                            width: '100%',
                                                                            padding: '0px',
                                                                        }}
                                                                    >
                                                                        <div className="field-label" style={{ marginBottom: '4px' }}>Open ID</div>
                                                                        <div className="field-value" style={{ width: '100%', wordBreak: 'break-all' }}>
                                                                            {selectedRow.uuid ? (
                                                                                <div className="field-value d-flex align-items-center gap-2">
                                                                                    <span
                                                                                        style={{
                                                                                            color: '#2C7BE5',
                                                                                            cursor: 'pointer',
                                                                                            textDecoration: 'underline',
                                                                                        }}
                                                                                        onClick={() => handleCopy(selectedRow.uuid)}
                                                                                    >
                                                                                        {selectedRow.uuid}
                                                                                        <Copy
                                                                                            size={13}
                                                                                            onClick={() => handleCopy(selectedRow.uuid)}
                                                                                            style={{ cursor: 'pointer', color: '#2C7BE5', marginLeft: '6px', }}
                                                                                        />
                                                                                    </span>
                                                                                    {copied && (
                                                                                        <span style={{ fontSize: '12px', color: '#2C7BE5' }}>
                                                                                            Copied!
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <span style={{ color: '#ADB5BD' }}>NA</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="field-block" style={{ borderBottom: 'none', padding: '0px' }}>
                                                                        <div className="field-label">Created By</div>
                                                                        <div className="field-value">{selectedRow.created_by_full_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                                    </div>

                                                                    <div className="field-block" style={{ borderBottom: 'none', padding: '0px' }}>
                                                                        <div className="field-label">Created Time</div>
                                                                        <div className="field-value">
                                                                            {formatDateTime(selectedRow.created_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </div>
                                                                    </div>

                                                                    <div className="field-block" style={{ borderBottom: 'none', padding: '0px' }}>
                                                                        <div className="field-label">Last Modified By</div>
                                                                        <div className="field-value">
                                                                            {selectedRow.modified_by_full_name || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </div>
                                                                    </div>

                                                                    <div className="field-block" style={{ borderBottom: 'none', padding: '0px' }}>
                                                                        <div className="field-label">Last Modified Time</div>
                                                                        <div className="field-value">
                                                                            {formatDateTime(selectedRow.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </div>
                                                                    </div>
                                                                    {activeTab !== "spu" && (
                                                                        <div className="field-block" style={{ borderBottom: 'none', padding: '0px' }}>
                                                                            <div className="field-label">Archived By</div>
                                                                            <div className="field-value">
                                                                                {selectedRow.archived_by_full_name || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {activeTab !== "spu" && (
                                                                        <div className="field-block" style={{ borderBottom: 'none', padding: '0px' }}>
                                                                            <div className="field-label">Archived Time</div>
                                                                            <div className="field-value">
                                                                                {formatDateTime(selectedRow.archived_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {/* ===== Business INFO CARD ===== */}
                                                            <div
                                                                className="card-section"
                                                                style={{
                                                                    borderRadius: '12px',
                                                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                <div
                                                                    className="card-header"
                                                                    style={{
                                                                        backgroundColor: '#F8F9FA',
                                                                        padding: '10px 16px',
                                                                        fontFamily: 'Montserrat',
                                                                        fontSize: '14px',
                                                                        fontWeight: 600,
                                                                        color: '#212529',
                                                                        borderBottom: 'none',
                                                                    }}
                                                                >
                                                                    Business Info
                                                                </div>

                                                                <div
                                                                    className="card-body"
                                                                    style={{
                                                                        backgroundColor: '#FFFFFF',
                                                                        padding: '10px 20px',
                                                                        display: 'grid',
                                                                        gridTemplateColumns: 'minmax(150px, 1fr) 1fr',
                                                                        gap: '15px',
                                                                        wordBreak: 'break-word',
                                                                        overflowWrap: 'break-word',
                                                                        whiteSpace: 'normal',
                                                                    }}
                                                                >

                                                                    <div className="field-block"
                                                                        style={{
                                                                            borderBottom: 'none',
                                                                            padding: '0px 10px',
                                                                            wordBreak: 'break-word',
                                                                            overflowWrap: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                        }}>
                                                                        <div className="field-label">GTIN</div>
                                                                        <div className="field-value">{selectedRow?.gtin || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                                    </div>
                                                                    <div className="field-block"
                                                                        style={{
                                                                            borderBottom: 'none',
                                                                            padding: '0px 10px',
                                                                            wordBreak: 'break-word',
                                                                            overflowWrap: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                        }}>
                                                                        <div className="field-label">MSRP</div>
                                                                        <div className="field-value">{selectedRow?.msrp ? `SGD $${selectedRow.msrp}` : <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {skuTab === 'configuration' && (
                                                        <div className="tab-panel configuration-panel">
                                                            {selectedRow?.configuration ? (
                                                                <>
                                                                    <div
                                                                        className="card-section"
                                                                        style={{
                                                                            borderRadius: '12px',
                                                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                                            overflow: 'hidden',
                                                                            marginTop: '40px',
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className="card-header"
                                                                            style={{
                                                                                backgroundColor: '#F8F9FA',
                                                                                padding: '10px 16px',
                                                                                fontFamily: 'Montserrat',
                                                                                fontSize: '14px',
                                                                                fontWeight: 600,
                                                                                color: '#212529',
                                                                                borderBottom: 'none',
                                                                            }}
                                                                        >
                                                                            Configuration Name
                                                                        </div>

                                                                        <div
                                                                            className="card-body"
                                                                            style={{
                                                                                backgroundColor: '#FFFFFF',
                                                                                padding: '16px',
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                gap: '12px',
                                                                                wordBreak: 'break-word',
                                                                                overflowWrap: 'break-word',
                                                                                whiteSpace: 'normal',
                                                                            }}
                                                                        >
                                                                            <div className="field-block" style={{ padding: "0px" }}>
                                                                                <div className="field-value">
                                                                                    {selectedRow.configuration.configuration_name || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Images Section */}
                                                                    {activeTab === "spu" ? 
                                                                    <div
                                                                        className="card-section"
                                                                        style={{
                                                                            borderRadius: '12px',
                                                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                                            overflow: 'hidden',
                                                                            marginTop: '20px',
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className="card-header"
                                                                            style={{
                                                                                backgroundColor: '#F8F9FA',
                                                                                padding: '10px 16px',
                                                                                fontFamily: 'Montserrat',
                                                                                fontSize: '14px',
                                                                                fontWeight: 600,
                                                                                color: '#212529',
                                                                                borderBottom: 'none',
                                                                            }}
                                                                        >
                                                                            Images
                                                                        </div>
                                                                        {(() => {
                                                                            const hasImages =
                                                                                Array.isArray(selectedRow.configuration.images) &&
                                                                                selectedRow.configuration.images.length > 0;

                                                                            return (
                                                                                <div
                                                                                    className="field-value"
                                                                                    style={{
                                                                                        width: "100%",
                                                                                        display: "flex",
                                                                                        gap: 10,
                                                                                        flexWrap: "wrap",
                                                                                        padding: "10px"
                                                                                    }}
                                                                                >
                                                                                    {!hasImages && (
                                                                                        <img
                                                                                            src={noImage}
                                                                                            alt="no image"
                                                                                            style={{
                                                                                                width: 120,
                                                                                                height: 120,
                                                                                                objectFit: "cover",
                                                                                                borderRadius: 6,
                                                                                                opacity: 0.7,
                                                                                            }}
                                                                                            onClick={() => {
                                                                                                setPreviewImages([noImage]);
                                                                                                setPreviewIndex(0);
                                                                                                setImageModalOpen(true);
                                                                                            }}
                                                                                        />
                                                                                    )}

                                                                                    {hasImages &&
                                                                                        selectedRow?.configuration?.images?.map((imgObj, index) => {
                                                                                            const imageUrl = imgObj?.sku_image_name;
                                                                                            return (
                                                                                                <img
                                                                                                    key={imgObj?.sku_configuration_image_id}
                                                                                                    src={imageUrl}
                                                                                                    alt="configuration"
                                                                                                    style={{
                                                                                                        width: 120,
                                                                                                        height: 120,
                                                                                                        objectFit: "cover",
                                                                                                        borderRadius: 6,
                                                                                                        cursor: "pointer",
                                                                                                    }}
                                                                                                    onClick={() => {

                                                                                                        // For preview, use backend URLs too
                                                                                                        const previewList = selectedRow.configuration.images.map(
                                                                                                            (img) => img.sku_image_name
                                                                                                        );

                                                                                                        setPreviewImages(previewList);
                                                                                                        setPreviewIndex(index);
                                                                                                        setImageModalOpen(true);
                                                                                                    }}
                                                                                                    onError={(e) => (e.target.src = noImage)}
                                                                                                />
                                                                                            );
                                                                                        })}
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                     : ""}

                                                                    {/* <div
                                                                        className="card-section"
                                                                        style={{
                                                                            borderRadius: '12px',
                                                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                                            overflow: 'hidden',
                                                                            marginTop: '20px',
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className="card-header"
                                                                            style={{
                                                                                backgroundColor: '#F8F9FA',
                                                                                padding: '10px 16px',
                                                                                fontFamily: 'Montserrat',
                                                                                fontSize: '14px',
                                                                                fontWeight: 600,
                                                                                color: '#212529',
                                                                                borderBottom: 'none',
                                                                            }}
                                                                        >
                                                                            Customised Info
                                                                        </div>

                                                                        <div
                                                                            className="card-body"
                                                                            style={{
                                                                                backgroundColor: '#FFFFFF',
                                                                                padding: '16px',
                                                                                display: 'grid',
                                                                                gridTemplateColumns: 'repeat(2, 1fr)',
                                                                                gap: '16px',
                                                                            }}
                                                                        >
                                                                            {getConfigurationSpecs().map((spec, index) => (
                                                                                <div key={index} className="field-block" style={{ borderBottom: 'none', padding: '0px' }}>
                                                                                    <div className="field-label">{spec.attribute_name || 'Specification'}</div>
                                                                                    <div className="field-value">
                                                                                        {spec.attribute_value || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div> */}

                                                                    
                                                                    {/* Custom Fields Section */} 
                                                                    {
                                                                    selectedRow.configuration.custom_fields &&
                                                                        selectedRow.configuration.custom_fields.length > 0 && (
                                                                            <div
                                                                                className="card-section"
                                                                                style={{
                                                                                    borderRadius: '12px',
                                                                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                                                    overflow: 'hidden',
                                                                                    marginTop: '20px',
                                                                                }}
                                                                            >
                                                                                <div
                                                                                    className="card-header"
                                                                                    style={{
                                                                                        backgroundColor: '#F8F9FA',
                                                                                        padding: '10px 16px',
                                                                                        fontFamily: 'Montserrat',
                                                                                        fontSize: '14px',
                                                                                        fontWeight: 600,
                                                                                        color: '#212529',
                                                                                        borderBottom: 'none',
                                                                                    }}
                                                                                >
                                                                                    Customised Info
                                                                                </div>

                                                                                <div
                                                                                    className="card-body"
                                                                                    style={{
                                                                                        backgroundColor: '#FFFFFF',
                                                                                        padding: '16px',
                                                                                        display: 'grid',
                                                                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                                                                        gap: '16px',
                                                                                    }}
                                                                                >
                                                                                    {selectedRow.configuration.custom_fields.map((field, index) => (
                                                                                        <div key={index} className="field-block" style={{ borderBottom: 'none', padding: '0px' }}>
                                                                                            <div className="field-label">{field.field_name || 'Field'}</div>
                                                                                            <div className="field-value">
                                                                                                {field.field_data || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    }

                                                                    {/* Notes Section */}
                                                                    <div
                                                                        className="card-section"
                                                                        style={{
                                                                            borderRadius: '12px',
                                                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                                            overflow: 'hidden',
                                                                            marginTop: '20px',
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className="card-header"
                                                                            style={{
                                                                                backgroundColor: '#F8F9FA',
                                                                                padding: '10px 16px',
                                                                                fontFamily: 'Montserrat',
                                                                                fontSize: '14px',
                                                                                fontWeight: 600,
                                                                                color: '#212529',
                                                                                borderBottom: 'none',
                                                                            }}
                                                                        >
                                                                            {activeTab === "spu" ? "Notes" : "Note"}
                                                                        </div>

                                                                        <div
                                                                            className="card-body"
                                                                            style={{
                                                                                backgroundColor: '#FFFFFF',
                                                                                padding: '16px',
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                gap: '12px',
                                                                            }}
                                                                        >
                                                                            <div className="field-block" style={{ padding: "0px" }}>
                                                                                <div
                                                                                    className="field-value"
                                                                                    style={{
                                                                                        background: '#FFFFFF',
                                                                                        fontSize: '13px',
                                                                                        color: selectedRow.configuration.notes ? '#212529' : '#ADB5BD',
                                                                                        minHeight: '24px',
                                                                                    }}
                                                                                >
                                                                                    {selectedRow.configuration.notes || 'NA'}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="empty-state">
                                                                    <div className="empty-state-content">
                                                                        <div class="icon"><img
                                                                            src={noBrandImage}
                                                                            alt="avatar"
                                                                            className="avatar"
                                                                            style={{
                                                                                width: 120,
                                                                                height: 120,
                                                                                verticalAlign: 'middle'
                                                                            }}
                                                                        /></div>
                                                                        <h1 class="no-item-title mt-2">
                                                                            No Configuration Applied
                                                                        </h1>
                                                                        <p class="no-item-text">
                                                                            No Configuration Applied to this SKU
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {imageModalOpen && (
                                                        <div
                                                            style={{
                                                                position: "fixed",
                                                                inset: 0,
                                                                background: "rgba(0,0,0,0.35)",   // soft background blur/dim
                                                                backdropFilter: "blur(3px)",
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                                zIndex: 5000,
                                                            }}
                                                        >
                                                            {/* WHITE CARD */}
                                                            <div
                                                                style={{
                                                                    background: "#fff",
                                                                    borderRadius: "16px",
                                                                    padding: "24px",
                                                                    maxWidth: "650px",
                                                                    maxHeight: "80vh",
                                                                    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                                                                    position: "relative",
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                {/* IMAGE */}
                                                                <img
                                                                    src={previewImages[previewIndex]}
                                                                    alt="preview"
                                                                    style={{
                                                                        maxWidth: "100%",
                                                                        maxHeight: "70vh",
                                                                        objectFit: "contain",
                                                                        borderRadius: "12px",
                                                                    }}
                                                                />

                                                                {/* CLOSE BUTTON */}
                                                                {/* CLOSE BUTTON – top-right of screen */}
                                                                <button
                                                                    onClick={() => setImageModalOpen(false)}
                                                                    style={{
                                                                        position: "fixed",
                                                                        top: "0px",
                                                                        right: "0px",
                                                                        background: "white",
                                                                        // borderRadius: "50%",
                                                                        width: "40px",
                                                                        height: "40px",
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        border: "none",
                                                                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                                                        cursor: "pointer",
                                                                        zIndex: 6000,
                                                                    }}
                                                                >
                                                                    <X size={22} />
                                                                </button>

                                                            </div>

                                                            {/* LEFT NAV */}
                                                            {previewImages.length > 1 && (
                                                                <button
                                                                    onClick={() =>
                                                                        setPreviewIndex((i) => (i - 1 + previewImages.length) % previewImages.length)
                                                                    }
                                                                    style={{
                                                                        position: "fixed",
                                                                        left: "0px",
                                                                        top: "50%",
                                                                        transform: "translateY(-50%)",
                                                                        background: "white",
                                                                        // borderRadius: "50%",
                                                                        width: "40px",
                                                                        height: "40px",
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        border: "none",
                                                                        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                                                                        cursor: "pointer",
                                                                    }}
                                                                >
                                                                    <ChevronLeft size={28} />
                                                                </button>
                                                            )}

                                                            {/* RIGHT NAV */}
                                                            {previewImages.length > 1 && (
                                                                <button
                                                                    onClick={() =>
                                                                        setPreviewIndex((i) => (i + 1) % previewImages.length)
                                                                    }
                                                                    style={{
                                                                        position: "fixed",
                                                                        right: "0px",
                                                                        top: "50%",
                                                                        transform: "translateY(-50%)",
                                                                        background: "white",
                                                                        // borderRadius: "50%",
                                                                        width: "40px",
                                                                        height: "40px",
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        border: "none",
                                                                        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                                                                        cursor: "pointer",
                                                                    }}
                                                                >
                                                                    <ChevronRight size={28} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}

                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {archivedModalOpen && (
                                    <>
                                        <div className="drawer-overlay" />
                                        <div className="entity-drawer modal-popup-cls" ref={archivedRef}
                                            style={{
                                                maxWidth: 650,
                                                margin: "0 auto",
                                                background: "white",
                                                borderRadius: 12,
                                                zIndex: 1050,
                                                padding: 24,
                                                position: "fixed",
                                                top: "50%",
                                                left: "50%",
                                                transform: "translate(-50%, -50%)",
                                                boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                                height: "auto",
                                                right: "auto",
                                                overflow: "inherit",
                                            }}>
                                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                                <button className="close-btn" onClick={() => setArchivedModalOpen(false)}>
                                                    <X size={20} />
                                                </button>
                                            </div>
                                            <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>The SPU can't be archived</h3>
                                            <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>
                                                This SPU can't be archived because it's referenced by one or more SPUs. Please remove or reassign those SPUs before archiving.
                                            </p>
                                            <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                                <button className="btn-save" onClick={handleArchivedConfirm}>
                                                    OK
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmDisableModal
                isOpen={disableModalOpen}
                onCancel={() => {
                    setDisableModalOpen(false);
                    setActionSkuId(null);
                }}
                onConfirm={handleConfirmDisable}
                entityName="SKU"
            />

            {/* Notification Toast */}
            {notification.show && (
                <AlertNotification
                    type={notification.type}
                    recordName={notification.recordName}
                    action={notification.action}
                    onClose={handleCloseNotification}
                />
            )}
            <style>{`
                .entities-table th:nth-child(1)::after,
                .entities-table td:nth-child(1)::after {
                content: "";
                position: absolute;
                top: 0;
                right: 0;
                width: 5px;
                height: 100%;
                pointer-events: none;
                background: linear-gradient(
                    to right,
                    rgba(0, 0, 0, 0.12),
                    rgba(0, 0, 0, 0)
                );
                z-index: 1;
                }

                th.actionHeader{
                    width:5%;
                }

                .entities-table td.actionColumn::before {
                    content: "";
                    position: absolute;
                    left: -8px;
                    top: 0;
                    width: 8px;
                    height: 100%;
                    background: linear-gradient(
                        to left,
                        rgba(0, 0, 0, 0.12),
                        rgba(0, 0, 0, 0)
                    );
                    pointer-events: none;
                    }

                    .entities-table th.actionHeader::before {
                    content: "";
                    position: absolute;
                    left: -8px;
                    top: 0;
                    width: 8px;
                    height: 100%;
                    background: linear-gradient(
                        to left,
                        rgba(0, 0, 0, 0.12),
                        rgba(0, 0, 0, 0)
                    );
                    pointer-events: none;
                    }
            `}</style>
        </div >
    );
}